import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { API_ENDPOINT } from "../../../constants/constants";
import { getDbHeader } from "../../../utils2024/utils";

const CategorySelectorAccordions = ({
  title = "",
  onSelectionChange,
  selectedCategories: propSelectedCategories = null,
  isOpen = false,
}) => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Controlled vs uncontrolled mode
  const [internalSelectedCategories, setInternalSelectedCategories] = useState(
    []
  );
  const isControlled = propSelectedCategories !== null;
  const selectedCategories = isControlled
    ? propSelectedCategories
    : internalSelectedCategories;

  // ✅ Fetch categories when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_ENDPOINT}event/get-product-categories`,
          {
            method: "GET",
            ...getDbHeader(),
          }
        );

        if (!response.ok) throw new Error("Failed to load categories");
        const data = await response.json();

        // API gives { type, categories: [...] }
        setProductCategories(data?.categories || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  // ✅ Group data into Main → Category → Subcategory (keeping full format)
  const groupedData = useMemo(() => {
    if (!productCategories?.length) return {};

    return productCategories.reduce((acc, curr) => {
      const mainCategory = curr.MainCategory?.trim();
      const category = curr.Category?.trim();
      const subCategory = curr.SubCategory?.trim();

      if (!mainCategory) return acc;

      if (!acc[mainCategory]) {
        acc[mainCategory] = { name: mainCategory, categories: {} };
      }

      if (category) {
        if (!acc[mainCategory].categories[category]) {
          acc[mainCategory].categories[category] = {
            name: category,
            subcategories: [],
          };
        }

        if (subCategory) {
          acc[mainCategory].categories[category].subcategories.push({
            name: subCategory,
          });
        }
      }

      return acc;
    }, {});
  }, [productCategories]);

  // ✅ Check selection
  const isSubcategorySelected = useCallback(
    (sub, mainName, categoryName) =>
      selectedCategories.some(
        (sel) =>
          sel.mainCat === mainName &&
          sel.subCat === categoryName &&
          sel.subSubCat === sub.name
      ),
    [selectedCategories]
  );

  // ✅ Update selection
  const handleCheckboxChange = (sub, mainName, categoryName) => {
    const catObj = {
      mainCat: mainName,
      subCat: categoryName,
      subSubCat: sub.name,
    };

    const prev = isControlled
      ? propSelectedCategories
      : internalSelectedCategories;

    const exists = prev.some(
      (sel) =>
        sel.mainCat === catObj.mainCat &&
        sel.subCat === catObj.subCat &&
        sel.subSubCat === catObj.subSubCat
    );

    const newSelections = exists
      ? prev.filter(
          (sel) =>
            !(
              sel.mainCat === catObj.mainCat &&
              sel.subCat === catObj.subCat &&
              sel.subSubCat === catObj.subSubCat
            )
        )
      : [...prev, catObj];

    if (!isControlled) {
      setInternalSelectedCategories(newSelections);
    }
    onSelectionChange?.(newSelections);
  };

  // ✅ UI states
  if (!isOpen) return null;

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
        <Typography variant="body2" mt={1}>
          Loading categories...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Alert severity="error">
        {error.message || "Failed to load categories"}
      </Alert>
    );

  if (!Object.keys(groupedData).length)
    return <Alert severity="info">No categories available</Alert>;

  // ✅ Render UI
  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {Object.values(groupedData).map((mainCategory) => (
        <Accordion key={mainCategory.name}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              {mainCategory.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {Object.values(mainCategory.categories).map((category) => (
              <Accordion key={category.name} sx={{ ml: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {category.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {category.subcategories.map((sub) => (
                    <FormControlLabel
                      key={sub.name}
                      sx={{ width: "100%" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={isSubcategorySelected(
                            sub,
                            mainCategory.name,
                            category.name
                          )}
                          onChange={() =>
                            handleCheckboxChange(
                              sub,
                              mainCategory.name,
                              category.name
                            )
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">{sub.name}</Typography>
                      }
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

CategorySelectorAccordions.propTypes = {
  title: PropTypes.string,
  onSelectionChange: PropTypes.func,
  selectedCategories: PropTypes.array,
  isOpen: PropTypes.bool,
};

export default React.memo(CategorySelectorAccordions);
